/**
 * @file psd-handler.ts
 * @description Main entry point for PSD IO, delegating to specialized sub-modules.
 */

import { g, layers } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import type { ILayer } from '@hcie/core';
import { LayerData } from './format-interface';
import { loadPsdFile } from './psd/parser';
import { convertPsdToLayers } from './psd/renderer';
import { savePsdFile as savePsdFileBinary } from './psd/writer';
import { PSDInstance } from './psd/types';

export { loadPsdFile, convertPsdToLayers };

/**
 * High-level save function for PSD.
 */
export async function savePsdFile(layerList: (ILayer | LayerData)[], composite?: ImageData): Promise<Uint8Array | null> {
    return savePsdFileBinary(layerList, g.image_width, g.image_height, composite);
}

// ─── Browser/UI Coordination ──────────────────────────────

/**
 * Legacy/UI entry point for applying a parsed PSD to the main application state.
 */
export async function applyPsdToCanvas(psdObj: PSDInstance) {
  try {
    console.log('[PSD] Applying layers to canvas...');
    const result = await convertPsdToLayers(psdObj);
    
    if (!result.layers || result.layers.length === 0) {
      console.warn('No layers found in PSD or conversion failed.');
      return;
    }
    
    // Update global state
    g.image_width = result.width;
    g.image_height = result.height;
    
    layers.length = 0;
    result.layers.forEach(l => layers.push(l));
    
    if (layers.length === 0) g.initDefaultLayer();
    g.activeLayerIndex = Math.max(0, layers.length - 1);
    
    // Notify UI via Event Bus
    EventBus.emit('PSD_LOADED', { 
        width: result.width, 
        height: result.height, 
        layerCount: layers.length 
    });
    
    console.log('[PSD] Application complete.');
  } catch (err) {
    console.error('Error applying PSD to canvas:', err);
  }
}

